import { REPORT_CHART_LIMITS } from '../constants/reportCharts';

// Whitespace as the server reads it: JavaScript's `\s` lacks U+001C to U+001F and U+0085, and adds U+FEFF.
const SPACE_CHARACTERS = '\\t-\\r\\x1c-\\x20\\x85\\xa0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000';
const S = `[${SPACE_CHARACTERS}]`;
const SPACES = new RegExp(`${S}+`);
const OUTER_SPACES = new RegExp(`^${S}+|${S}+$`, 'g');
const serverRegExp = (pattern: string, flags = '') => new RegExp(pattern.replace(/\\s/g, SPACE_CHARACTERS), flags);

// Mirror of the server attribute allowlist: the attributes it keeps decide the render cost of each element.
const ID = '[A-Za-z_][A-Za-z0-9_.\\-]{0,127}';
const LOCAL_URL = `url\\([\\s]*#${ID}[\\s]*\\)`;
const COLOR = '(?:#[0-9A-Fa-f]{3,8}|[A-Za-z]{1,32}|(?:rgba?|hsla?)\\([0-9.,%+\\-\\s]{1,64}\\))';
const GEOMETRY = serverRegExp('^[0-9A-Za-z.,+\\-%\\s]*$');
const PATH_DATA = serverRegExp('^[MmLlHhVvCcSsQqTtAaZz0-9eE.,+\\-\\s]*$');
const KEYWORD = serverRegExp('^[A-Za-z0-9.\\-\\s]*$');
const TRANSFORM = serverRegExp('^(?:[\\s]*(?:matrix|translate|scale|rotate|skewX|skewY)\\([0-9eE.,+\\-\\s]*\\)[\\s]*,?)*[\\s]*$');
const COLOR_VALUE = serverRegExp(`^[\\s]*${COLOR}[\\s]*$`);
const PAINT = serverRegExp(`^[\\s]*(?:${COLOR}|${LOCAL_URL}(?:[\\s]+${COLOR})?)[\\s]*$`);
const FUNC_IRI = serverRegExp(`^[\\s]*(?:none|${LOCAL_URL})[\\s]*$`);
const ID_VALUE = new RegExp(`^${ID}$`);
const REFERENCES = serverRegExp(`url\\([\\s]*#${ID}[\\s]*\\)`, 'g');
const SINGLE_SUBPATH = serverRegExp('^[\\s]*[Mm][^MmZz]*[Zz]?[\\s]*$');
// Path data tokens, read from left to right: a command, a number, separators, or any other character.
const PATH_TOKEN = serverRegExp('([MmLlHhVvCcSsQqTtAaZz])|([+-]?(?:[0-9]+\\.?[0-9]*|\\.[0-9]+)(?:[eE][+-]?[0-9]+)?)|[\\s,]+|(.)', 'gs');
// Numbers per argument group of each command; an arc's 1st and 2nd numbers are its radii, its 4th and 5th flags.
const PATH_ARITY: Record<string, number> = { m: 2, l: 2, t: 2, h: 1, v: 1, c: 6, s: 4, q: 4, a: 7, z: 0 };
const ARC_FLAG_POSITIONS = [3, 4];
const REPEATED_CLOSES = serverRegExp('([Zz])(?:[\\s,]*[Zz])+', 'g');
const NUMBER_TOKEN = /(?:[0-9]+\.?[0-9]*|\.[0-9]+)(?:[eE][-+]?[0-9]+)?/g;
const DASH_VALUE = /^(?:[0-9]+\.?[0-9]*|\.[0-9]+)(?:[eE][-+]?[0-9]+)?$/;
const LIST_SEPARATOR = serverRegExp('[\\s,]+');
const SIGNED = '[+-]?(?:[0-9]+\\.?[0-9]*|\\.[0-9]+)(?:[eE][+-]?[0-9]+)?';
const SIGNED_NUMBER = new RegExp(`^${SIGNED}$`);
// Four plain numbers, the last two the width and height.
const VIEW_BOX = serverRegExp(`^[\\s]*${SIGNED}[\\s,]+${SIGNED}[\\s,]+(${SIGNED})[\\s,]+(${SIGNED})[\\s]*$`);
const TRANSFORM_FUNCTION = /(matrix|translate|scale|rotate|skewX|skewY)\(([^)]*)\)/g;
const TRANSFORM_ARGUMENT_COUNTS: Record<string, number[]> = {
  matrix: [6],
  translate: [1, 2],
  scale: [1, 2],
  rotate: [1, 3],
  skewX: [1],
  skewY: [1],
};
const DEGREE = Math.PI / 180;
// The browser and the server may round a sine or tangent one unit apart in the last place: within this margin over
// the bound such a matrix counts as kept, the costlier estimate.
const TRIGONOMETRIC = /rotate|skew/;
const MATRIX_TOLERANCE = 1 + 1e-13;
const DASH_PADDING = /^[ ,]+|[ ,]+$/g;
// Words the renderer reads as infinite or undefined numbers, which no range check sees.
const NON_FINITE = /inf|nan/i;
const LARGE_NUMBER_HINT = /\d{7}|[eE][-+]?\d/;
const GRADIENT_LENGTH = /^([+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)(%|px)?$/;
// Renderer defaults of the coordinates that set a gradient's extent.
const GRADIENT_DEFAULTS: Record<string, string> = { x1: '0', y1: '0', x2: '100%', y2: '0', r: '50%' };
// Rounding margin on the extent thresholds: at the boundary a gradient counts as drawn, the costlier estimate.
const EXTENT_TOLERANCE = 1 - 1e-9;

const SHAPES = new Set(['path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon']);
const MARKER_HOSTS = new Set(['path', 'line', 'polyline', 'polygon']);
export const GEOMETRY_ATTRIBUTES = ['d', 'points'];

const GEOMETRY_NAMES = (
  'x y x1 y1 x2 y2 cx cy r rx ry fx fy dx dy width height viewBox points refX refY markerWidth ' +
  'markerHeight offset opacity fill-opacity stroke-opacity stop-opacity stroke-width stroke-dasharray ' +
  'stroke-dashoffset stroke-miterlimit font-size letter-spacing baseline-shift version'
).split(' ');
const KEYWORD_NAMES = (
  'fill-rule clip-rule stroke-linecap stroke-linejoin text-anchor dominant-baseline alignment-baseline ' +
  'font-weight font-style text-decoration visibility display shape-rendering vector-effect ' +
  'gradientUnits clipPathUnits markerUnits preserveAspectRatio orient'
).split(' ');
const STYLE_NAMES = (
  'opacity fill-opacity stroke-opacity stroke-width stroke-dasharray font-size letter-spacing ' +
  'stroke-linecap stroke-linejoin text-anchor dominant-baseline font-weight font-style text-decoration ' +
  'visibility display'
).split(' ');

const ATTRIBUTE_RULES = new Map<string, RegExp>([
  ...GEOMETRY_NAMES.map((name): [string, RegExp] => [name, GEOMETRY]),
  ['d', PATH_DATA],
  ...KEYWORD_NAMES.map((name): [string, RegExp] => [name, KEYWORD]),
  ['transform', TRANSFORM],
  ['gradientTransform', TRANSFORM],
  ['fill', PAINT],
  ['stroke', PAINT],
  ['stop-color', COLOR_VALUE],
  ['color', COLOR_VALUE],
  ['clip-path', FUNC_IRI],
  ['marker-start', FUNC_IRI],
  ['marker-end', FUNC_IRI],
  ['id', ID_VALUE],
]);
const RANGE_CHECKED_ATTRIBUTES = [...ATTRIBUTE_RULES]
  .filter(([, rule]) => [GEOMETRY, PATH_DATA, TRANSFORM, KEYWORD].includes(rule))
  .map(([name]) => name);
// Inline style declarations are kept as attributes, colours without any url().
const STYLE_RULES = new Map<string, RegExp>([
  ...STYLE_NAMES.map((name): [string, RegExp] => [name, ATTRIBUTE_RULES.get(name)!]),
  ['fill', COLOR_VALUE],
  ['stroke', COLOR_VALUE],
  ['color', COLOR_VALUE],
]);

const collapse = (value: string) => value.split(SPACES).filter(Boolean).join(' ');
const strip = (value: string) => value.replace(OUTER_SPACES, '');

const styleDeclarations = (style: string): [string, string][] => {
  if (style.length > REPORT_CHART_LIMITS.maxSvgStyleLength) return [];
  return style.split(';').flatMap((declaration): [string, string][] => {
    const separator = declaration.indexOf(':');
    if (separator === -1) return [];
    const name = strip(declaration.slice(0, separator)).toLowerCase();
    const value = collapse(declaration.slice(separator + 1));
    const rule = STYLE_RULES.get(name);
    return rule && value && rule.test(value) ? [[name, value]] : [];
  });
};

/** A dash pattern of a few plain numbers, each zero or long enough, summing to the minimum pattern length. */
const dashPatternOk = (value: string) => {
  const dashes = value.replace(DASH_PADDING, '').split(LIST_SEPARATOR);
  if (dashes.length > REPORT_CHART_LIMITS.maxDashValues || !dashes.every((dash) => DASH_VALUE.test(dash))) return false;
  const lengths = dashes.map(Number);
  return (
    lengths.every((length) => length === 0 || length >= REPORT_CHART_LIMITS.minDashLength) &&
    lengths.reduce((sum, length) => sum + length, 0) >= REPORT_CHART_LIMITS.minSvgDashPatternLength
  );
};

type Matrix = [number, number, number, number, number, number];

/**
 * Matrix of a transform list, its functions composed as the server does (each applied before those on its left);
 * `null` when a function has another number of arguments than its own or one that is not a plain number.
 */
const transformMatrix = (value: string): Matrix | null => {
  let [a, b, c, d, e, f] = [1, 0, 0, 1, 0, 0];
  for (const [, name, args] of value.matchAll(TRANSFORM_FUNCTION)) {
    const texts = strip(args).split(LIST_SEPARATOR);
    if (!TRANSFORM_ARGUMENT_COUNTS[name].includes(texts.length) || !texts.every((text) => SIGNED_NUMBER.test(text))) return null;
    const x = texts.map(Number);
    let step: number[];
    if (name === 'matrix') step = x;
    else if (name === 'translate') step = [1, 0, 0, 1, x[0], x.length === 2 ? x[1] : 0];
    else if (name === 'scale') step = [x[0], 0, 0, x[x.length - 1], 0, 0];
    else if (name === 'rotate') {
      const [cos, sin] = [Math.cos(x[0] * DEGREE), Math.sin(x[0] * DEGREE)];
      const [cx, cy] = x.length === 3 ? [x[1], x[2]] : [0, 0];
      step = [cos, sin, -sin, cos, cx - cos * cx + sin * cy, cy - sin * cx - cos * cy];
    } else {
      const tangent = Math.tan(x[0] * DEGREE);
      step = name === 'skewX' ? [1, 0, tangent, 1, 0, 0] : [1, tangent, 0, 1, 0, 0];
    }
    const [sa, sb, sc, sd, se, sf] = step;
    [a, b, c, d, e, f] = [sa * a + sb * c, sa * b + sb * d, sc * a + sd * c, sc * b + sd * d, se * a + sf * c + e, se * b + sf * d + f];
  }
  return [a, b, c, d, e, f];
};

/** Whether a transform matrix is within the bound and, for a gradient, invertible with its inverse within it too. */
const matrixInRange = (name: string, matrix: Matrix, bound: number) => {
  const inRange = (coefficients: number[]) => coefficients.every((coefficient) => Math.abs(coefficient) <= bound);
  if (!inRange(matrix)) return false;
  if (name !== 'gradientTransform') return true;
  const [a, b, c, d, e, f] = matrix;
  const determinant = a * d - b * c;
  return determinant !== 0 && inRange([d, -b, -c, a, c * f - d * e, b * e - a * f].map((x) => x / determinant));
};

/**
 * Whether every number is finite and within the server bound, a dash pattern not too fine for it, a viewBox four
 * numbers large enough and a transform's matrix (and a gradient's inverse matrix) within the bound.
 */
const numbersInRange = (name: string, value: string) => {
  if (NON_FINITE.test(value)) return false;
  const numbers = (value.match(NUMBER_TOKEN) ?? []).map(Number);
  if (LARGE_NUMBER_HINT.test(value) && numbers.some((n) => n > REPORT_CHART_LIMITS.maxSvgAbsNumber)) return false;
  if (name === 'viewBox') {
    const viewBox = VIEW_BOX.exec(value);
    return viewBox !== null && Math.min(Number(viewBox[1]), Number(viewBox[2])) >= REPORT_CHART_LIMITS.minViewBoxSize;
  }
  if (name === 'transform' || name === 'gradientTransform') {
    const matrix = transformMatrix(value);
    const bound = REPORT_CHART_LIMITS.maxSvgAbsNumber * (TRIGONOMETRIC.test(value) ? MATRIX_TOLERANCE : 1);
    return matrix !== null && matrixInRange(name, matrix, bound);
  }
  return name !== 'stroke-dasharray' || value === 'none' || dashPatternOk(value);
};

interface GradientLength {
  value: number;
  /** A fraction of the bounding box (or a percentage), else user units. */
  fraction: boolean;
}

const gradientLength = (value: string, boundingBox: boolean): GradientLength | null => {
  const match = GRADIENT_LENGTH.exec(value);
  if (!match) return null;
  return match[2] === '%' ? { value: Number(match[1]) / 100, fraction: true } : { value: Number(match[1]), fraction: boundingBox };
};

const longEnough = (length: number, fraction: boolean) =>
  length >= (fraction ? REPORT_CHART_LIMITS.minGradientFraction : REPORT_CHART_LIMITS.minGradientUserLength) * EXTENT_TOLERANCE;

/**
 * Whether the server keeps the id of a gradient (from its kept attributes): its vector (linear) or radius (radial),
 * defaults included, is long enough to be drawn; an unreadable coordinate, or one mixing percentages and user units
 * on an axis, is not.
 */
export const gradientExtentOk = (tag: string, attributes: Map<string, string>): boolean => {
  const boundingBox = attributes.get('gradientUnits') !== 'userSpaceOnUse';
  const coordinate = (name: string) => gradientLength(attributes.get(name) ?? GRADIENT_DEFAULTS[name], boundingBox);
  if (tag === 'radialGradient') {
    const radius = coordinate('r');
    return radius !== null && longEnough(radius.value, radius.fraction);
  }
  const components = { fraction: [] as number[], user: [] as number[] };
  for (const [start, end] of [
    ['x1', 'x2'],
    ['y1', 'y2'],
  ]) {
    const first = coordinate(start);
    const last = coordinate(end);
    if (!first || !last || (first.value && last.value && first.fraction !== last.fraction)) return false;
    const fraction = last.value ? last.fraction : first.fraction;
    components[fraction ? 'fraction' : 'user'].push(last.value - first.value);
  }
  return longEnough(Math.hypot(...components.fraction), true) || longEnough(Math.hypot(...components.user), false);
};

/** A paint or reference value without its references: the fallback colour, possibly empty. */
export const referenceFallback = (value: string) => strip(value.replace(REFERENCES, ''));

/** Whether an arc's radii are both zero, or both positive with a ratio the server accepts. */
const arcRadiiOk = (rx: number, ry: number) => {
  const [low, high] = [Math.min(rx, ry), Math.max(rx, ry)];
  return !high || (low > 0 && high / low <= REPORT_CHART_LIMITS.maxArcRadiiRatio);
};

/**
 * Whether a `d` is empty or a move followed by commands, each with a whole number of argument groups (none for a
 * close), arc flags written `0` or `1` and arc radii accepted: the server drops any other `d`, and the path then draws
 * nothing.
 */
export const wellFormedPathData = (d: string): boolean => {
  let arity: number | null = null;
  let count = 0;
  let radius = 0;
  for (const [, letter, number, other] of d.matchAll(PATH_TOKEN)) {
    if (other !== undefined) return false;
    if (letter !== undefined) {
      if ((arity === null && letter !== 'M' && letter !== 'm') || (arity && (count === 0 || count % arity))) return false;
      arity = PATH_ARITY[letter.toLowerCase()];
      count = 0;
    } else if (number !== undefined) {
      const position = count % 7;
      if (!arity) return false;
      if (arity === 7 && ARC_FLAG_POSITIONS.includes(position) && number !== '0' && number !== '1') return false;
      if (arity === 7 && position === 0) radius = Number(number);
      if (arity === 7 && position === 1 && !arcRadiiOk(radius, Number(number))) return false;
      count += 1;
    }
  }
  return !arity || (count > 0 && count % arity === 0);
};

/** Drops the references an element may not hold, keeping the fallback colour of a paint. */
const dropReferences = (attributes: Map<string, string>, tag: string, inResource: boolean) => {
  const singleSubpath = SINGLE_SUBPATH.test(attributes.get('d') ?? '');
  if (inResource || !MARKER_HOSTS.has(tag) || (tag === 'path' && !singleSubpath)) {
    attributes.delete('marker-start');
    attributes.delete('marker-end');
  }
  if (inResource) attributes.delete('clip-path');
  if (!inResource && SHAPES.has(tag)) return;
  ['fill', 'stroke'].forEach((name) => {
    const value = attributes.get(name);
    const fallback = value === undefined ? undefined : referenceFallback(value);
    if (fallback) attributes.set(name, fallback);
    else attributes.delete(name);
  });
};

/** Attributes of an element as the server keeps them (allowlist, value rules, path grammar, inline style, number bounds, references). */
export const keptAttributes = (element: Element, inResource: boolean): Map<string, string> => {
  const kept = new Map<string, string>();
  Array.from(element.attributes)
    .filter((attribute) => attribute.namespaceURI === null)
    .forEach(({ name, value }) => {
      const rule = ATTRIBUTE_RULES.get(name);
      const tooLong = !GEOMETRY_ATTRIBUTES.includes(name) && value.length > REPORT_CHART_LIMITS.maxSvgAttributeLength;
      if (rule && !tooLong && rule.test(value)) kept.set(name, collapse(value));
    });
  const d = kept.get('d');
  if (d !== undefined) {
    // Consecutive closes draw as one: the server merges them before weighing the path.
    if (wellFormedPathData(d)) kept.set('d', d.replace(REPEATED_CLOSES, '$1'));
    else kept.delete('d');
  }
  styleDeclarations(element.getAttribute('style') ?? '').forEach(([name, value]) => kept.set(name, value));
  RANGE_CHECKED_ATTRIBUTES.forEach((name) => {
    const value = kept.get(name);
    if (value !== undefined && !numbersInRange(name, value)) kept.delete(name);
  });
  dropReferences(kept, element.localName, inResource);
  return kept;
};
